import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router';
import { useSession } from '../lib/SignaSessionContext';
import { applyThemeMode, readThemeMode, type ThemeMode } from '../lib/theme';
import { APP_VERSION } from '../version';
import { Button } from './ui/Button';

// El shell de la app autenticada: navegación, quién sos, y la versión a la vista.
//
// UNA SOLA NAVEGACIÓN para las dos superficies (policies/ux-ui-design.md §5, «un solo modo por
// superficie»): el workspace y el constructor no son dos modos que se alternan con un toggle —
// son dos destinos, y el constructor sólo aparece si el rol de Signa lo habilita. Los roles salen
// de Signa y nada más (Regla de Oro 3); la UI no tiene modelo de permisos propio, y esconder un
// link NO es el control: el control lo aplica `nexa-api` (`requireRole('owner','admin')`).

interface NavItem {
  to: string;
  label: string;
  /** Sin roles ⇒ visible para toda persona con sesión. */
  roles?: string[];
  iconPath: string;
}

// RUTAS EN INGLÉS (policies/architecture.md §3): las URLs son identificadores técnicos.
// Las ETIQUETAS van en español rioplatense. Las dos reglas conviven en esta tabla a propósito.
const NAV: NavItem[] = [
  { to: '/inbox', label: 'Bandeja', iconPath: 'M3 12h5l2 3h4l2-3h5M4 5h16v14H4z' },
  { to: '/cases', label: 'Casos', iconPath: 'M4 6h16M4 12h16M4 18h10' },
  { to: '/builder/forms', label: 'Constructor', roles: ['owner', 'admin'], iconPath: 'M4 20h16M6 16l6-10 6 10' },
  { to: '/admin/settings', label: 'Configuración', roles: ['owner', 'admin'], iconPath: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm8-3a8 8 0 0 1-.1 1.2l2 1.5-2 3.4-2.3-.9a8 8 0 0 1-2 1.2l-.4 2.4h-4l-.4-2.4a8 8 0 0 1-2-1.2l-2.3.9-2-3.4 2-1.5A8 8 0 0 1 4 12c0-.4 0-.8.1-1.2l-2-1.5 2-3.4 2.3.9a8 8 0 0 1 2-1.2L8.8 3h4l.4 2.6a8 8 0 0 1 2 1.2l2.3-.9 2 3.4-2 1.5c.1.4.1.8.1 1.2Z' },
];

export function AppShell() {
  const { user, hasRole, logout } = useSession();
  const location = useLocation();
  const visible = NAV.filter((item) => !item.roles || hasRole(...item.roles));

  return (
    <div className="flex min-h-full flex-col">
      <header className="sticky top-0 z-40 border-b border-white/20 bg-white/70 backdrop-blur-xl
                         dark:border-zinc-800/50 dark:bg-zinc-950/70">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between gap-4">
            <div className="flex min-w-0 items-center gap-6">
              <span className="text-lg font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">Nexa</span>
              <nav aria-label="Secciones" className="hidden items-center gap-1 sm:flex">
                {visible.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) => navClasses(isActive || location.pathname.startsWith(sectionOf(item.to)))}
                  >
                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.6"
                      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d={item.iconPath} />
                    </svg>
                    {item.label}
                  </NavLink>
                ))}
              </nav>
            </div>

            <div className="flex items-center gap-3">
              <ThemeToggle />
              <UserChip name={user?.name ?? user?.email ?? 'Sesión'} org={user?.orgName} />
              <Button variant="ghost" onClick={logout}>Salir</Button>
            </div>
          </div>

          {/* En pantallas chicas la navegación baja a su propia fila, sin esconderse en un menú:
              la bandeja tiene que estar a un toque de distancia. */}
          <nav aria-label="Secciones" className="flex items-center gap-1 overflow-x-auto pb-3 sm:hidden">
            {visible.map((item) => (
              <NavLink key={item.to} to={item.to} className={({ isActive }) => navClasses(isActive)}>
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="border-t border-white/20 py-8 dark:border-zinc-800/50">
        <div className="w-full max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-2 px-4 text-xs
                        text-zinc-500 sm:px-6 lg:px-8 dark:text-zinc-500">
          {/* La versión, siempre visible: es el primer dato que hace falta cuando algo no anda. */}
          <span>Nexa {APP_VERSION}</span>
          <span>{user?.orgName ?? user?.orgId}</span>
        </div>
      </footer>
    </div>
  );
}

function sectionOf(path: string): string {
  const [, first] = path.split('/');
  return `/${first ?? ''}`;
}

function navClasses(isActive: boolean): string {
  return [
    'inline-flex h-10 shrink-0 items-center gap-2 rounded-xl px-3 text-sm font-medium transition-colors duration-300',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400',
    isActive
      ? 'bg-zinc-900 text-white dark:bg-white dark:text-zinc-900'
      : 'text-zinc-600 hover:bg-zinc-900/5 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-white/10 dark:hover:text-zinc-100',
  ].join(' ');
}

function UserChip({ name, org }: { name: string; org?: string }) {
  return (
    <span className="hidden min-w-0 flex-col items-end sm:flex">
      <span className="truncate text-sm font-medium text-zinc-800 dark:text-zinc-200">{name}</span>
      {org && <span className="truncate text-xs text-zinc-500 dark:text-zinc-400">{org}</span>}
    </span>
  );
}

const THEME_LABEL: Record<ThemeMode, string> = {
  light: 'Tema claro',
  dark: 'Tema oscuro',
  auto: 'Tema automático',
};

const THEME_ORDER: ThemeMode[] = ['auto', 'light', 'dark'];

function ThemeToggle() {
  const [mode, setMode] = useState<ThemeMode>('auto');

  // Se lee en un efecto y no en el estado inicial para que el render del servidor/test no dependa
  // de `localStorage`; el bootstrap de `index.html` ya pintó el tema correcto antes de esto.
  useEffect(() => setMode(readThemeMode()), []);

  function cycle() {
    const next = THEME_ORDER[(THEME_ORDER.indexOf(mode) + 1) % THEME_ORDER.length] as ThemeMode;
    setMode(next);
    applyThemeMode(next);
  }

  return (
    <button
      type="button"
      onClick={cycle}
      aria-label={`${THEME_LABEL[mode]}. Cambiar tema.`}
      className="inline-flex h-10 w-10 items-center justify-center rounded-xl text-zinc-600 transition-colors duration-300
                 hover:bg-zinc-900/5 hover:text-zinc-900 focus-visible:outline-none focus-visible:ring-2
                 focus-visible:ring-zinc-400 dark:text-zinc-400 dark:hover:bg-white/10 dark:hover:text-zinc-100"
    >
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.6"
        strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {mode === 'dark'
          ? <path d="M20 13a8 8 0 1 1-9-9 6.5 6.5 0 0 0 9 9Z" />
          : mode === 'light'
            ? <path d="M12 4V2m0 20v-2m8-8h2M2 12h2m13.7-5.7 1.4-1.4M4.9 19.1l1.4-1.4m11.4 0 1.4 1.4M4.9 4.9l1.4 1.4M16 12a4 4 0 1 1-8 0 4 4 0 0 1 8 0Z" />
            : <path d="M12 3v18a9 9 0 0 0 0-18Zm0 0a9 9 0 0 0 0 18" />}
      </svg>
    </button>
  );
}
